import { useEffect, useState } from "react"
import type { PortalRequestResponse } from "@ledgerbox/domain"

import { isSentBack, itemsToFixLabel, nextItemToFix } from "./item-state"

type Item = PortalRequestResponse["items"][number]

/** The item a press of the header button named. Each press is a new
 * object, so a second press on the same item lands again. */
export type ItemToFix = { itemId: string }

/**
 * "3 items to fix" in the portal header, while the firm has items sent
 * back. It stays on a phone and on every section, so the client can press
 * it from anywhere. Each press names the next one; the page opens it, and
 * `useLandOnItem` scrolls to it and puts focus inside it.
 */
export function ItemsToFixButton({
  items,
  onGo,
}: {
  /** Every item, in the order the page lists them. */
  items: readonly Item[]
  onGo(itemId: string): void
}) {
  const [presses, setPresses] = useState(0)
  const count = items.filter(isSentBack).length
  if (!count) return null
  return (
    <button
      type="button"
      className="text-button items-to-fix"
      onClick={() => {
        const next = nextItemToFix(items, presses)
        if (!next) return
        setPresses((current) => current + 1)
        onGo(next.itemId)
      }}
    >
      <span className="items-to-fix-dot" aria-hidden="true" />
      {itemsToFixLabel(count)}
    </button>
  )
}

/**
 * Lands on the item the header button named, once the page has drawn it
 * open: scrolls it into view and moves focus inside it, so a keyboard or
 * screen-reader user lands on the item, not just near it. `elementId` names
 * the element that takes focus; `onLanded` clears the target, so a page
 * drawn again later does not jump.
 */
export function useLandOnItem(
  target: ItemToFix | null | undefined,
  elementId: (itemId: string) => string,
  onLanded?: () => void
) {
  useEffect(() => {
    if (!target) return
    const node = document.getElementById(elementId(target.itemId))
    onLanded?.()
    if (!node) return
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    node.scrollIntoView({ block: "center", behavior: still ? "auto" : "smooth" })
    node.focus({ preventScroll: true })
    // Only a new press lands; the id and callback are read at that moment.
  }, [target])
}
